import api from './api';
import publicApi from './publicApi';

export interface File {
  id: number;
  tenant_id: number;
  patient_id?: number;
  meeting_id?: number;
  patient_questionnaire_id?: number;
  uploaded_by_user_id: number;
  file_name: string;
  file_type: string;
  file_size: number;
  s3_key: string;
  description?: string;
  question_name?: string;
  file_type_id?: number;
  created_at: string;
  updated_at: string;
  uploaded_by_name?: string;
  patient_name?: string;
  meeting_title?: string;
  file_type_name?: string;
}

export interface FileListResponse {
  items: File[];
  total: number;
}

export const fileService = {
  async uploadFile(
    file: globalThis.File,
    description?: string,
    patientId?: number,
    meetingId?: number,
    patientQuestionnaireId?: number,
    questionName?: string,
    fileTypeId?: number
  ): Promise<File> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    if (patientId) formData.append('patient_id', patientId.toString());
    if (meetingId) formData.append('meeting_id', meetingId.toString());
    if (patientQuestionnaireId) formData.append('patient_questionnaire_id', patientQuestionnaireId.toString());
    if (questionName) formData.append('question_name', questionName);
    if (fileTypeId) formData.append('file_type_id', fileTypeId.toString());

    const response = await api.post<File>('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async uploadPublicFile(
    token: string,
    file: globalThis.File,
    questionName?: string
  ): Promise<File> {
    const formData = new FormData();
    formData.append('file', file);
    if (questionName) formData.append('question_name', questionName);

    const response = await publicApi.post<File>(
      `/public/questionnaires/${token}/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  async getFiles(patientId?: number, meetingId?: number): Promise<FileListResponse> {
    const params: Record<string, number> = {};
    if (patientId) params.patient_id = patientId;
    if (meetingId) params.meeting_id = meetingId;

    const response = await api.get<FileListResponse>('/files/', { params });
    return response.data;
  },

  async getFile(fileId: number): Promise<File> {
    const response = await api.get<File>(`/files/${fileId}`);
    return response.data;
  },

  async updateFile(fileId: number, description?: string, fileTypeId?: number): Promise<File> {
    const formData = new FormData();
    if (description !== undefined) formData.append('description', description);
    if (fileTypeId !== undefined) formData.append('file_type_id', fileTypeId.toString());

    const response = await api.patch<File>(`/files/${fileId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async deleteFile(fileId: number): Promise<void> {
    await api.delete(`/files/${fileId}`);
  },

  async downloadFile(fileId: number): Promise<void> {
    // Download file directly from backend
    const response = await api.get(`/files/${fileId}/download`, {
      responseType: 'blob',
    });

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];

    let fileName = 'download';
    if (contentDisposition) {
      // Try RFC 5987 format first (filename*=UTF-8''...)
      const rfc5987Match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
      if (rfc5987Match && rfc5987Match[1]) {
        fileName = decodeURIComponent(rfc5987Match[1]);
      } else {
        // Fallback to standard format (filename="...")
        const standardMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (standardMatch && standardMatch[1]) {
          fileName = standardMatch[1];
        }
      }
    }

    // Create blob URL and trigger download
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  async getFilePreview(fileId: number): Promise<{ content: string; mimeType: string }> {
    try {
      // Try to get the file as a blob
      const response = await api.get(`/files/${fileId}/download`, {
        responseType: 'blob',
      });

      const blob = response.data;
      const mimeType = blob.type || 'application/octet-stream';

      // Convert blob to base64
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          // Remove the data URL prefix (e.g., "data:image/png;base64,")
          const base64Content = base64String.split(',')[1];
          resolve({ content: base64Content, mimeType });
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Failed to load file preview:', error);
      throw error;
    }
  },
};
